// Presentation helpers for measured values (tokens, cost, time). Unknown values are never
// rendered as zero: callers pass null and get an explicit "unknown" label (RF-OBS-03).
import { useEffect, useState } from "react";
import type { Precision } from "@maestros/contracts";

/** Cost in cents with up to 4 decimals (e.g. "12¢", "0.35¢"). */
export function formatCents(cents: number, locale: string): string {
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: cents < 1 ? 4 : 2 }).format(cents)}¢`;
}

export function formatNumber(n: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(n);
}

/** Duration with precision down to milliseconds for short spans. */
export function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const total = Math.round(s);
  const m = Math.floor(total / 60);
  if (m < 60) return `${m}m ${String(total % 60).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

export function formatCountdown(ms: number): string {
  if (ms <= 0) return "0s";
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  if (m < 60) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
}

/** Current time that re-renders every `intervalMs` (countdowns, elapsed time of open spans). */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function elapsedMs(startIso: string, endIso: string | null, now: number): number | null {
  const start = Date.parse(startIso);
  if (Number.isNaN(start)) return null;
  const end = endIso ? Date.parse(endIso) : now;
  return Number.isNaN(end) ? null : Math.max(0, end - start);
}

export const PRECISION_TONE: Record<Precision, "success" | "info" | "warning" | "neutral"> = {
  measured: "success",
  estimated: "info",
  incomplete: "warning",
  unknown: "neutral",
};

export function isPrecision(v: string): v is Precision {
  return v === "measured" || v === "estimated" || v === "incomplete" || v === "unknown";
}

/** Parse an optional numeric input: empty → null, invalid → undefined. */
export function parseOptionalNumber(raw: string): number | null | undefined {
  const s = raw.trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/** Known server-side progress scope labels (ProjectSummary.progress.scope_label) and their i18n keys (D36 audit). */
const SCOPE_LABEL_KEYS = { "All tasks": "projects.scopeAll" } as const;

/** The progress scope label in the UI language; labels the UI does not know (e.g. "PRD v3") are shown as sent. */
export function scopeLabel(t: (key: (typeof SCOPE_LABEL_KEYS)[keyof typeof SCOPE_LABEL_KEYS]) => string, raw: string): string {
  return raw in SCOPE_LABEL_KEYS ? t(SCOPE_LABEL_KEYS[raw as keyof typeof SCOPE_LABEL_KEYS]) : raw;
}
